/* The Atelier List pop-up and footer sign-up — see sections/atelier-list.liquid and atelier-list-signup.liquid */
(() => {
  // Both sections load this file; run it once.
  if (window.atelierListLoaded) return;
  window.atelierListLoaded = true;

  const STORAGE_KEY = 'atelierList:hideUntil';
  const HIDE_DAYS = 14;
  const SCROLL_RATIO = 0.5;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

  const storage = {
    get() {
      try {
        return Number(window.localStorage.getItem(STORAGE_KEY)) || 0;
      } catch (e) {
        return 0;
      }
    },
    suppress() {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(Date.now() + HIDE_DAYS * 864e5));
      } catch (e) {}
    },
  };

  // Which form was submitted, so a footer error doesn't open the pop-up (both are customer forms
  // and Shopify reports the result to every customer form on the reloaded page).
  const SOURCE_KEY = 'atelierList:source';
  const FORM_SOURCES = { AtelierListForm: 'popup', AtelierListFooterForm: 'footer' };
  const source = {
    get() {
      try {
        return window.sessionStorage.getItem(SOURCE_KEY);
      } catch (e) {
        return null;
      }
    },
    set(value) {
      try {
        window.sessionStorage.setItem(SOURCE_KEY, value);
      } catch (e) {}
    },
  };
  const lastSource = source.get();

  document.addEventListener('submit', (e) => {
    const name = FORM_SOURCES[e.target.id];
    if (!name) return;
    source.set(name);
    // Joining from either form means the pop-up shouldn't ask again.
    storage.suppress();
  });

  // Copy buttons in either success state.
  document.addEventListener('click', async (e) => {
    const button = e.target.closest('[data-atelier-copy]');
    if (!button) return;
    const codeEl = button.parentElement.querySelector('[data-atelier-code]');
    const code = codeEl?.textContent.trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch (err) {
      const range = document.createRange();
      range.selectNodeContents(codeEl);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      document.execCommand('copy');
    }
    const label = button.dataset.label || button.textContent;
    button.dataset.label = label;
    button.textContent = button.dataset.copiedLabel || 'Copied';
    window.setTimeout(() => (button.textContent = label), 2000);
  });

  // A footer submission error belongs to the footer; a pop-up error to the pop-up.
  if (lastSource === 'popup') {
    document.querySelectorAll('[data-atelier-footer-error]').forEach((el) => (el.hidden = true));
  }

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class AtelierList extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('dialog');
      this.dialog = this.querySelector('.atelier-list__dialog');
      this.form = this.closest('form');
      this.isOpen = false;
      this.armed = false;

      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onMouseOut = this.onMouseOut.bind(this);

      // Esc: animate out through close() instead of the native instant close.
      this.modal.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });
      this.querySelectorAll('[data-atelier-close]').forEach((el) => el.addEventListener('click', () => this.close()));

      // Any link to #atelier-list opens the pop-up directly, e.g. from the announcement bar.
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href$="#atelier-list"]');
        if (!link) return;
        e.preventDefault();
        this.open(link);
      });

      // After a submission the page reloads: show the result straight away.
      // Success from either form opens the welcome state; errors only reopen the pop-up if it was the one used.
      if (this.dataset.posted === 'true') {
        storage.suppress();
        this.open();
        return;
      }
      if (this.dataset.errors === 'true' && lastSource !== 'footer') {
        this.open();
        return;
      }

      if (window.Shopify?.designMode) {
        // Theme editor: preview while the section is selected, never auto-open or suppress.
        const section = this.closest('.shopify-section');
        document.addEventListener('shopify:section:select', (e) => e.target === section && this.open());
        document.addEventListener('shopify:section:deselect', (e) => e.target === section && this.close());
        return;
      }
      // A direct link to /any-page#atelier-list opens it, even if previously dismissed.
      if (window.location.hash === '#atelier-list') {
        this.open();
        return;
      }
      if (storage.get() > Date.now()) return;
      this.arm();
    }

    arm() {
      this.armed = true;
      this.timer = window.setTimeout(() => this.autoOpen(), Number(this.dataset.delay) || 10000);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      if (window.matchMedia('(min-width: 990px) and (pointer: fine)').matches) {
        document.addEventListener('mouseout', this.onMouseOut);
      }
    }

    disarm() {
      this.armed = false;
      window.clearTimeout(this.timer);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('mouseout', this.onMouseOut);
    }

    onScroll() {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (max > 0 && window.scrollY / max >= SCROLL_RATIO) this.autoOpen();
    }

    onMouseOut(e) {
      if (!e.relatedTarget && e.clientY <= 0) this.autoOpen();
    }

    autoOpen() {
      if (!this.armed) return;
      // Don't interrupt another open drawer or modal.
      if (document.querySelector('modal-window[open], .drawer.active, details[open] > .drawer, cart-drawer.active')) return;
      this.open();
    }

    open(opener) {
      if (this.isOpen) return;
      this.disarm();
      this.isOpen = true;
      this.lastFocus = opener || document.activeElement;
      this.modal.showModal();
      document.documentElement.classList.add('atelier-list-open');
      document.addEventListener('keydown', this.onKeydown);
      // Force a reflow so the fade/slide transition runs from the closed state.
      void this.modal.offsetWidth;
      this.modal.classList.add('is-open');
      const target = this.querySelector('[aria-invalid="true"]') || this.querySelector('.atelier-list__input') || this.dialog;
      window.setTimeout(() => target.focus({ preventScroll: true }), reducedMotion() ? 0 : 60);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (!window.Shopify?.designMode) storage.suppress();
      this.modal.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.classList.remove('atelier-list-open');
      const finish = () => {
        this.modal.close();
        if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
      };
      if (reducedMotion()) finish();
      else window.setTimeout(finish, 420);
    }

    onKeydown(e) {
      if (e.key !== 'Tab') return;
      const items = Array.from(this.dialog.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (document.activeElement === last || !this.dialog.contains(document.activeElement))) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('atelier-list', AtelierList);
})();
